export const userScopedKey = (userId, key) => `clairifi:${userId}:${key}`;

/** basic KV */
export function setUserCache(userId, key, value) {
  if (!userId) return;
  try { localStorage.setItem(userScopedKey(userId, key), JSON.stringify(value)); } catch (_) {}
}
export function getUserCache(userId, key, fallback = null) {
  if (!userId) return fallback;
  try {
    const raw = localStorage.getItem(userScopedKey(userId, key));
    return raw ? JSON.parse(raw) : fallback;
  } catch (_) { return fallback; }
}

export function clearUserCache(userId) {
  if (!userId) return;
  try {
    const prefix = `clairifi:${userId}:`;
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(prefix)) localStorage.removeItem(k);
    }
  } catch (_) {}
}

/** SWR-style helpers for uploads list (local mirror + TTL) */
const TTL_UPLOADS_MS = 24 * 60 * 60 * 1000; // 24h

function _now() { return Date.now(); }
function _wrap(value) { return { value, cached_at: _now() }; }
function _fresh(entry, ttlMs) { return entry && (typeof entry.cached_at === "number") && (_now() - entry.cached_at < ttlMs); }

export function saveUploadsLocal(userId, uploadsArray) {
  if (!userId) return;
  try {
    localStorage.setItem(userScopedKey(userId, "uploads_cache"), JSON.stringify(_wrap(uploadsArray)));
  } catch (_) {}
}
export function getUploadsLocal(userId) {
  if (!userId) return { value: [], fresh: false };
  try {
    const raw = localStorage.getItem(userScopedKey(userId, "uploads_cache"));
    const parsed = raw ? JSON.parse(raw) : null;
    return { value: parsed?.value || [], fresh: _fresh(parsed, TTL_UPLOADS_MS) };
  } catch (_) { return { value: [], fresh: false }; }
}

/**
 * getUploadsWithFallback(fetchFn, userId)
 * - Immediately returns cached uploads (may be stale)
 * - Triggers background fetchFn() to refresh and saves result to local mirror
 *
 * Usage in a React component:
 *   const { value: initialUploads } = getUploadsWithFallback(() => api.getUploads(), user.id);
 *   // render initialUploads; when fetch resolves, update state with setter
 */
export function getUploadsWithFallback(fetchFn, userId, onRefresh) {
  const cached = getUploadsLocal(userId);
  try {
    Promise.resolve(fetchFn()).then(list => {
      if (Array.isArray(list)) {
        saveUploadsLocal(userId, list);
        if (typeof onRefresh === "function") onRefresh(list);
      }
    }).catch(() => {});
  } catch (_e) {}
  return cached;
}
// --- Account dashboard helpers ---
export function getActiveUpload() {
  try {
    return localStorage.getItem("activeUploadFilename") || "";
  } catch {
    return "";
  }
}
export function setActiveUpload(filename) {
  try {
    localStorage.setItem("activeUploadFilename", filename || "");
  } catch {}
}

export function getHistoryClearedAt() {
  try {
    return localStorage.getItem("historyClearedAt") || "";
  } catch {
    return "";
  }
}
export function setHistoryClearedAt(iso) {
  try {
    localStorage.setItem("historyClearedAt", iso || "");
  } catch {}
}

export function getLocallyHiddenFilenames() {
  try {
    const raw = localStorage.getItem("hiddenFilenames");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
export function setLocallyHiddenFilenames(list) {
  try {
    localStorage.setItem("hiddenFilenames", JSON.stringify(list || []));
  } catch {}
}
export function getAuthToken() {
  try {
    // prefer new key, keep old for backward compatibility
    return localStorage.getItem('clairifi_token') || localStorage.getItem('token') || '';
  } catch {
    return '';
  }
}
